// src/components/Resources/ResourceCard.js
import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CalendarIcon, Wrench, BookOpen, Users, Flag } from 'lucide-react';
import { useTranslations } from '../../translations/translations';

const ResourceCard = ({ resource, onFilterClick }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const t = useTranslations();

  const truncateText = (text, maxLength) => {
    if (text.length <= maxLength) return text;
    return text.substr(0, maxLength) + '...';
  }
  
  const processImageUrl = (url) => {
    if (!url) return 'placeholder.svg';
    return url;
  }

  const getBadgeIcon = (type) => {
    switch (type) {
      case 'tipo':
        return <Wrench className="h-3 w-3 mr-1" />;
      case 'tema':
        return <BookOpen className="h-3 w-3 mr-1" />;
      case 'organizacion':
        return <Users className="h-3 w-3 mr-1" />;
      case 'pais':
        return <Flag className="h-3 w-3 mr-1" />;
      default:
        return null;
    }
  }

  const handleBadgeClick = (filterType, value) => {
    if (onFilterClick) {
      onFilterClick(filterType, value);
    }
  }

  const handleCardClick = (e) => {
    if (!e.target.closest('.badge') && !e.target.closest('button')) {
      window.open(resource.link, '_blank', 'noopener,noreferrer');
    }
  }

  return (
    <Card className="h-full flex flex-col border-none">
      <CardHeader className="space-y-3">
        <img 
          src={processImageUrl(resource.img_link)}
          alt={resource.title}
          className="w-full h-48 object-contain mb-4 cursor-pointer  bg-white"
          onClick={handleCardClick}
        />    
        {
            resource.year ? (
              <div className="text-sm text-primary font-bold flex items-center mb-4">
                <CalendarIcon size={14} className="mr-1 mb-1" />
                <span>{resource.year}</span>
              </div>
            ) : (
              <div className="text-sm text-primary flex items-center py-3  mb-4">
              </div>
            )
        }  
        <CardTitle className="transition-colors hover:text-primary cursor-pointer text-2xl font-medium"  onClick={handleCardClick}>
          {resource.title}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex-grow">
        <p className="text-sm text-gray-600 mb-4 text-md transition-colors">
          {isExpanded ? resource.description : truncateText(resource.description, 200)}
          {resource.description && resource.description.length > 200 && (
            <Button
              variant="link"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              className="p-0 h-auto font-normal"
            >
              {isExpanded ? t.card.readLess : t.card.readMore}
            </Button>
          )}
        </p>
        <div className="mb-4 flex flex-wrap gap-2">
          {/* Badges para tipo (puede ser array) */}
          {Array.isArray(resource.tipo) ? (
            resource.tipo.map((tipo, index) => (
              <Badge
                key={`tipo-${index}`}
                className="flex items-center font-medium bg-white text-black hover:bg-accent hover:text-white border-none cursor-pointer badge"
                onClick={(e) => {
                  e.stopPropagation();
                  handleBadgeClick('tipo', tipo);
                }}
              >
                <Wrench className="h-3 w-3 mr-1" />
                {tipo}
              </Badge>
            ))
          ) : resource.tipo && (
            <Badge
              className="flex items-center font-medium bg-white text-black hover:bg-accent hover:text-white border-none cursor-pointer badge"
              onClick={(e) => {
                e.stopPropagation();
                handleBadgeClick('tipo', resource.tipo);
              }}
            >
              <Wrench className="h-3 w-3 mr-1" />
              {resource.tipo}
            </Badge>
          )}

          {/* Badges para tema (puede ser array) */}
          {Array.isArray(resource.tema) ? (
            resource.tema.map((tema, index) => (
              <Badge
                key={`tema-${index}`}                
                className="flex items-center font-medium bg-white text-black hover:bg-accent hover:text-white border-none cursor-pointer badge"
                onClick={(e) => {
                  e.stopPropagation();
                  handleBadgeClick('tema', tema);
                }}
              >
                <BookOpen className="h-3 w-3 mr-1" />
                {tema}
              </Badge>
            ))
          ) : resource.tema && (
            <Badge
              className="flex items-center font-medium bg-white text-black hover:bg-accent hover:text-white border-none cursor-pointer badge"
              onClick={(e) => {
                e.stopPropagation();
                handleBadgeClick('tema', resource.tema);
              }}
            >
              <BookOpen className="h-3 w-3 mr-1" />
              {resource.tema}
            </Badge>
          )}

          {/* Badges para organización y país (siempre strings simples) */}
          {resource.organizacion && (
            <Badge
              className="flex items-center font-medium bg-white text-black hover:bg-accent hover:text-white border-none cursor-pointer badge"
              onClick={(e) => {
                e.stopPropagation();
                handleBadgeClick('organizacion', resource.organizacion);
              }}
            >
              <Users className="h-3 w-3 mr-1" />
              {resource.organizacion}
            </Badge>
          )}

          {resource.pais && (
            <Badge
              className="flex items-center font-medium bg-white text-black hover:bg-accent hover:text-white border-none cursor-pointer badge"
              onClick={(e) => {
                e.stopPropagation();
                handleBadgeClick('pais', resource.pais);
              }}
            >
              <Flag className="h-3 w-3 mr-1" />
              {resource.pais}
            </Badge>
          )}
        </div>  
      </CardContent>
      <CardFooter>
        <Button 
          className="mx-auto px-12 text-sm text-white font-semibold hover:border-2 hover:border-accent hover:bg-accent hover:text-white hover:font-medium"
          variant="default"
          size="lg"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            window.open(resource.link, '_blank', 'noopener,noreferrer');
          }}
        >
          {t.card.visitResource}
        </Button>
      </CardFooter>
    </Card>
  );
}

export default ResourceCard;